import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '../../components/PageHeader';
import {
  fetchTestDb,
  fetchTestPrisma,
  fetchLastUpdated,
  fetchScripts,
  fetchScriptRuns,
  fetchSourceFreshness,
  fetchClaudeStatus,
  triggerScript,
  timeAgo,
  isActiveStatus,
} from './serverStatusQueries';

const FRESHNESS_SOURCE_LABELS: Record<string, string> = {
  cfia: 'CFIA (CA)',
  efsa: 'EFSA',
  favv: 'FAVV (BE)',
  favv_news: 'FAVV news (BE)',
  fsanz: 'FSANZ (AU/NZ)',
  fsa_uk: 'UK FSA',
  fsis: 'USDA FSIS',
  lebensmittelwarnung: 'Lebensmittelwarnung (DE)',
  mrl_us: 'US EPA MRLs (40 CFR 180)',
  nvwa: 'NVWA (NL)',
  openfda: 'US FDA',
  rappelconso: 'RappelConso (FR)',
};

function runStatusBadge(status: string) {
  const cls =
    status === 'success' ? 'staging-badge--approved' :
    status === 'failed' ? 'staging-badge--rejected' :
    'staging-badge--pending';

  const label =
    status === 'claimed' ? 'Starting...' :
    status === 'running' ? 'Running...' :
    status;

  return <span className={`staging-badge ${cls}`}>{label}</span>;
}

export default function SystemStatusPage() {
  const queryClient = useQueryClient();
  const [expandedRun, setExpandedRun] = useState<number | null>(null);

  const { data: dbStatus, isLoading: dbLoading, isError: dbError, error: dbErrorObj } =
    useQuery<{ ok: boolean }, Error>(['testDb'], fetchTestDb);

  const { data: prismaStatus, isLoading: prismaLoading, isError: prismaError, error: prismaErrorObj } =
    useQuery<{ ok: boolean }, Error>(['testPrisma'], fetchTestPrisma);

  const { data: lastUpdatedData, isLoading: lastUpdatedLoading, isError: lastUpdatedError, error: lastUpdatedErrorObj } =
    useQuery(['lastUpdated'], fetchLastUpdated);

  const { data: freshnessData } = useQuery(['sourceFreshness'], fetchSourceFreshness);

  const { data: claude, isError: claudeError, error: claudeErrorObj } =
    useQuery(['claudeStatus'], fetchClaudeStatus);

  const { data: scripts } = useQuery(['scripts'], fetchScripts);

  const { data: scriptRuns } = useQuery(['scriptRuns'], fetchScriptRuns, {
    refetchInterval: (data) => (data?.some(r => isActiveStatus(r.status)) ? 2000 : false),
  });

  const triggerMut = useMutation(triggerScript, {
    onSuccess: () => {
      queryClient.invalidateQueries(['scriptRuns']);
    },
  });

  const loading = dbLoading || prismaLoading || lastUpdatedLoading;
  const hasActiveRun = scriptRuns?.some(r => isActiveStatus(r.status));

  return (
    <div className="container admin-page">
      <PageHeader
        title="System Status"
        tagline="Connection health, ingestion scripts, and run history."
        back={{ to: '/admin', label: 'Back to Manage' }}
      />

      {/* Connection checks */}
      <section className="card admin-section">
        <div className="card-header">
          <h3>Connection Status</h3>
        </div>
        <div className="card-body admin-section__body">
          {loading && <div className="admin-muted">Checking…</div>}

          {!dbLoading && (
            <div className="status-row">
              <span className={`status-dot ${!dbError && dbStatus?.ok !== false ? 'status-dot--ok' : 'status-dot--err'}`} />
              <span className="status-row__label">Database Connection</span>
              {dbError && <span className="status-row__error">{dbErrorObj?.message}</span>}
            </div>
          )}

          {!prismaLoading && (
            <div className="status-row">
              <span className={`status-dot ${!prismaError && prismaStatus?.ok !== false ? 'status-dot--ok' : 'status-dot--err'}`} />
              <span className="status-row__label">Prisma ORM</span>
              {prismaError && <span className="status-row__error">{prismaErrorObj?.message}</span>}
            </div>
          )}

          {(claude || claudeError) && (
            <div className="status-row">
              <span className={`status-dot ${claude?.ok ? 'status-dot--ok' : 'status-dot--err'}`} />
              <span className="status-row__label">Claude CLI (subscription)</span>
              {claude?.ok && (
                <span className="admin-muted" style={{ fontSize: '12px' }}>
                  {[claude.version, claude.auth?.subscriptionType, claude.busy ? 'busy' : 'idle']
                    .filter(Boolean).join(' · ')}
                  {claude.lock && ` · lock held by ${claude.lock.owner} ${claude.lock.label ?? ''}`}
                </span>
              )}
              {!claude?.ok && (
                <span className="status-row__error">
                  {claudeError
                    ? (claudeErrorObj as Error)?.message
                    : claude?.error
                      || (claude?.auth?.loggedIn === false
                        ? 'not logged in: run `claude`, then /login, in an SSH shell on the server'
                        : 'unavailable')}
                </span>
              )}
            </div>
          )}
          {claude && (
            <p className="admin-muted" style={{ fontSize: '12px', marginTop: '4px' }}>
              Models: Opus {claude.models.opus} (writing, judging) · Haiku {claude.models.haiku} (extraction,
              translation) · effort {claude.effort}.
              Every Claude call runs through the server&apos;s Claude Code CLI on the subscription (no API key).
            </p>
          )}
        </div>
      </section>

      {/* Source Freshness */}
      <section className="card admin-section">
        <div className="card-header">
          <h3>Source Freshness</h3>
        </div>
        <div className="card-body admin-section__body">
          {freshnessData?.gate && (
            <div className="status-row">
              <span className="status-dot status-dot--err" />
              <span className="status-row__label">
                Nightly rebuild (curated_schema){' '}
                {freshnessData.gate.status === 'failure' ? 'failing' : 'finishing with problems'}{' '}
                since {new Date(freshnessData.gate.since).toLocaleDateString('en-GB')}
              </span>
              <span className="admin-muted">
                {freshnessData.gate.last_alerted_at
                  ? `emailed ${timeAgo(freshnessData.gate.last_alerted_at)}`
                  : 'not emailed yet'}
              </span>
            </div>
          )}
          {!freshnessData || freshnessData.sources.length === 0 ? (
            <div className="admin-muted">
              No freshness data yet — the monitor runs at the end of the nightly pipeline.
            </div>
          ) : (
            <>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Source</th>
                    <th>Newest item</th>
                    <th>Silent for</th>
                    <th>Threshold</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {freshnessData.sources.map(s => (
                    <tr key={s.source}>
                      <td>{FRESHNESS_SOURCE_LABELS[s.source] || s.source}</td>
                      <td>{s.newest_published ?? '—'}</td>
                      <td>{s.days_since === null ? '—' : `${s.days_since}d`}</td>
                      <td>{s.threshold_days}d</td>
                      <td>
                        <span className="status-row status-row--inline">
                          <span className={`status-dot ${
                            s.is_stale ? 'status-dot--err' : s.parked ? 'status-dot--muted' : 'status-dot--ok'
                          }`} />
                          {s.is_stale ? 'STALE' : s.parked ? 'parked' : s.informational ? 'low-cadence' : 'fresh'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {freshnessData.sources.filter(s => s.parked && s.note).map(s => (
                <p key={s.source} className="admin-muted" style={{ fontSize: '12px', marginTop: '8px' }}>
                  <strong>{FRESHNESS_SOURCE_LABELS[s.source] || s.source}:</strong> {s.note}
                </p>
              ))}
              {freshnessData.checkedAt && (
                <p className="admin-muted" style={{ fontSize: '12px', marginTop: '8px' }}>
                  Checked {timeAgo(freshnessData.checkedAt)} — a source is stale when its silence
                  exceeds ~1.5× its own p90 publish gap (clamped to 7–30 days). Filtered
                  authority-news channels (Sciensano, FAVV news) are low-cadence by nature:
                  assessed and shown, never alerted. A parked source is blocked at its end
                  (data_ingestion/pipeline_parked.py): not run nightly and never alerted; its
                  loader is probed every Monday, and the monitor emails when a probe gets
                  through. The same monitor also emails on the night the curated_schema
                  rebuild fails or finishes with problems.
                </p>
              )}
            </>
          )}
        </div>
      </section>

      {/* Script Runner */}
      {scripts && (
        <section className="card admin-section">
          <div className="card-header">
            <h3>Run Ingestion Scripts</h3>
          </div>
          <div className="card-body admin-section__body">
            <div className="script-grid">
              {scripts.map(s => {
                const isRunAll = s.name === 'run_all';
                return (
                  <button
                    key={s.name}
                    className={`btn script-card ${isRunAll ? 'btn--primary' : 'btn--secondary'}`}
                    onClick={() => triggerMut.mutate(s.name)}
                    disabled={!!hasActiveRun || triggerMut.isLoading}
                    title={s.description}
                  >
                    <span className="script-card__label">{s.label}</span>
                    <span className="script-card__desc">{s.description}</span>
                  </button>
                );
              })}
            </div>

            {scriptRuns && scriptRuns.length > 0 && (
              <div className="run-history">
                <h4 className="run-history__title">Run history</h4>
                {scriptRuns.map(run => (
                  <div
                    key={run.id}
                    className={`run-item ${isActiveStatus(run.status) ? 'run-item--active' : ''}`}
                  >
                    <div
                      className="run-item__header"
                      onClick={() => setExpandedRun(expandedRun === run.id ? null : run.id)}
                    >
                      <span className="run-item__name">
                        {scripts.find(s => s.name === run.script_name)?.label || run.script_name}
                      </span>
                      <div className="run-item__meta">
                        <span className="run-item__time">{timeAgo(run.created_at)}</span>
                        {runStatusBadge(run.status)}
                      </div>
                    </div>

                    {isActiveStatus(run.status) && run.status !== 'pending' && (
                      <div className="run-item__progress">
                        <div className="run-item__progress-bar" />
                      </div>
                    )}

                    {run.status === 'running' && run.output && (
                      <div className="run-item__step">{run.output}</div>
                    )}

                    {expandedRun === run.id && run.output && run.status !== 'running' && (
                      <pre className="run-item__output">{run.output}</pre>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Ingestion Logs */}
      {!lastUpdatedLoading && (
        <section className="card admin-section">
          <div className="card-header">
            <h3>Ingestion Logs</h3>
          </div>
          <div className="card-body admin-section__body">
            {lastUpdatedError ? (
              <div className="admin-error">Error: {lastUpdatedErrorObj?.message}</div>
            ) : (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Script</th>
                    <th>Run Time</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {lastUpdatedData!.lastRuns.map(run => (
                    <tr key={`${run.script_name}-${run.run_time}`}>
                      <td>{run.script_name}</td>
                      <td>{new Date(run.run_time).toLocaleString('en-GB')}</td>
                      <td>
                        <span className="status-row status-row--inline">
                          <span className={`status-dot ${
                            run.status === 'success' ? 'status-dot--ok'
                              : run.status === 'parked' ? 'status-dot--muted' : 'status-dot--err'
                          }`} />
                          {run.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
